import { useEffect, useState } from 'react';
import { Modal, ModalContent, ModalBody, Button } from '@heroui/react';
import toast from 'react-hot-toast';
import { FaXmark, FaCheck, FaLink } from 'react-icons/fa6';
import { FiRefreshCw } from 'react-icons/fi';
import repositories from '../../repositories.jsx';
import YouTubeBadge, { GoogleG } from './primitives/YouTubeBadge.jsx';

/* Google → YouTube connect flow (PRD §10.2). "Allow" kicks off the real
   OAuth round-trip: the backend returns a consent URL and Google redirects
   back to `returnTo` with ?channel_connect=success|error.
   props:
     mode            'channel' | 'source' | 'dest'  (copy only — Google owns the picker)
     returnTo        path the backend should bounce back to after OAuth
     beforeRedirect  called just before leaving the page (park builder state)
     isOpen, onClose
     connectedIds / onConnected   accepted for call-site compatibility (unused) */
export default function ConnectChannelModal({ mode = 'channel', returnTo, beforeRedirect, isOpen, onClose, connectedIds = [], onConnected }) { // eslint-disable-line no-unused-vars
	const [step, setStep] = useState('consent'); // consent | connecting

	// reset whenever the modal re-opens
	useEffect(() => {
		if (isOpen) setStep('consent');
	}, [isOpen]);

	const allow = async () => {
		setStep('connecting');
		try {
			beforeRedirect?.();
			const data = await repositories.channel.youtubeConnect({ return_to: returnTo ?? '/channels' });
			if (data?.auth_url) {
				window.location.href = data.auth_url;
				return;
			}
			throw new Error('missing auth_url');
		} catch {
			toast.error('Could not start the connection. Please try again.');
			onClose();
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="md" hideCloseButton placement="center" classNames={{ base: 'rounded-3xl' }}>
			<ModalContent>
				<ModalBody className="p-0 overflow-hidden">
					{/* google-style header */}
					<div className="px-6 py-[18px] border-b border-slate-100 flex items-center gap-3">
						<GoogleG size={22} />
						<div className="text-[15px] font-semibold text-slate-800">Sign in with Google</div>
						<button onClick={onClose} className="ml-auto w-[34px] h-[34px] rounded-full grid place-items-center text-pink-500 hover:bg-pink-50 transition">
							<FaXmark size={18} />
						</button>
					</div>

					<div className="p-6">
						{step === 'consent' && (
							<div>
								<div className="flex items-center justify-center gap-3.5 mb-4">
									<img src="/images/logo-icon.png" alt="AudioDub" className="w-[46px] h-[46px] object-contain" />
									<FaLink size={16} className="text-slate-400" />
									<YouTubeBadge size={40} />
								</div>
								<h3 className="text-center text-[18px] font-semibold text-slate-800 mb-1">AudioDub wants to access<br />your YouTube account</h3>
								<p className="text-center text-[12px] text-slate-500 mb-4">This lets AudioDub:</p>
								<div className="flex flex-col gap-2.5 mb-5">
									{['View your channels, video list & metadata',
									  'Get notified when new videos are uploaded',
									  'Upload, publish & manage multi-language audio tracks'].map((p, i) => (
										<div key={i} className="flex gap-3 items-start">
											<div className="w-[22px] h-[22px] rounded-full bg-emerald-50 text-emerald-600 grid place-items-center flex-none mt-px"><FaCheck size={12} /></div>
											<div className="text-[13.5px] text-slate-600 leading-snug">{p}</div>
										</div>
									))}
								</div>
								<div className="flex gap-2.5">
									<Button variant="bordered" radius="full" className="flex-1" onPress={onClose}>Cancel</Button>
									<Button color="primary" radius="full" className="flex-1" onPress={allow}>Allow</Button>
								</div>
							</div>
						)}

						{step === 'connecting' && (
							<div className="text-center py-6">
								<FiRefreshCw size={38} className="text-primary mx-auto animate-spin" />
								<h3 className="mt-4 mb-1 text-[17px] font-semibold text-slate-800">Connecting…</h3>
								<p className="text-[12px] text-slate-500">Securely taking you to Google to link your YouTube account.</p>
							</div>
						)}
					</div>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}
